import type { Mutation } from '@tahi/core';
import { outbox } from '@tahi/db';
import { and, gte, inArray, sql } from 'drizzle-orm';
import type { LocalDb } from './localDb';
import { emitTables } from './tableBus';

export async function readOutbox(db: LocalDb, limit = 20): Promise<Mutation[]> {
  const rows = await db
    .select({ mutation: outbox.mutation })
    .from(outbox)
    .orderBy(sql`rowid`)
    .limit(limit)
    .all();
  return rows.map((r) => r.mutation);
}

export async function countOutbox(db: LocalDb): Promise<number> {
  const row = await db
    .select({ n: sql<number>`count(*)` })
    .from(outbox)
    .get();
  return Number(row?.n ?? 0);
}

export async function removeFromOutbox(db: LocalDb, ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  await db.delete(outbox).where(inArray(outbox.mutationId, ids));
  emitTables(['outbox']);
}

/** A mutation the server keeps failing on is dropped after this many attempts (v0.1b sync changes). */
export const MAX_OUTBOX_ATTEMPTS = 5;

/**
 * Records a server-side failure (`failed`) for these mutations: attempts + 1, last error stored.
 * Returns the ids that have now reached MAX_OUTBOX_ATTEMPTS; the sync client drops those like a final rejection.
 */
export async function markOutboxFailed(
  db: LocalDb,
  ids: string[],
  error: string,
): Promise<string[]> {
  if (ids.length === 0) return [];
  await db
    .update(outbox)
    .set({ attempts: sql`${outbox.attempts} + 1`, lastError: error })
    .where(inArray(outbox.mutationId, ids));
  const rows = await db
    .select({ id: outbox.mutationId })
    .from(outbox)
    .where(and(inArray(outbox.mutationId, ids), gte(outbox.attempts, MAX_OUTBOX_ATTEMPTS)))
    .all();
  emitTables(['outbox']);
  return rows.map((r) => r.id);
}
